// eslint-disable-next-line no-restricted-imports
import { useTheme } from '@emotion/react';
// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { clsx } from 'clsx';
import noop from 'lodash-es/noop';
import { useCallback, useLayoutEffect } from 'react';
import type { Props as ReactModalProps } from 'react-modal';
import ReactModal from 'react-modal';

import { SvgIcon } from '../../component-library/svg-icon';
import { useCustomerDispatch } from '../../customer/dispatch';
import {
  closeModal as defaultCloseModal,
  selectApplication,
} from '../../customer/redux/application';
import { useCustomerSelector } from '../../customer/selectors';
import styles from './modal.module.css';

const bodyElement = document.body;

if (process.env['NODE_ENV'] !== 'test') {
  ReactModal.setAppElement('#app');
}

interface Props extends Omit<
  ReactModalProps,
  'isOpen' | 'onRequestClose' | 'children'
> {
  children: (closeModal: () => void) => JSX.Element;
  className?: string;
  closeModal?: () => void;
  contentLabel: string;
  disallowClose?: boolean;
  fixedBody?: boolean;
  width?: 'default' | 'full' | 'medium' | 'narrow' | 'small';
}

const CloseButton = styled.button(({ theme }) => ({
  position: 'absolute' as const,
  top: 24,
  right: 24,
  height: 16,
  '@media (hover: hover)': {
    '&:hover': {
      background: theme?.colors?.grays?.ultralight,
      height: 32,
      top: 16,
      right: 16,
      borderColor: theme?.colors?.grays?.ultralight,
      borderWidth: 8,
      borderStyle: 'solid',
      borderRadius: 30,
    },
  },
}));

export const Modal = ({
  className,
  contentLabel,
  disallowClose,
  width,
  children,
  closeModal,
  fixedBody = false,
  ...rest
}: Props) => {
  const theme = useTheme();
  const { isModalOpen, modalOptions } = useCustomerSelector(selectApplication);
  const dispatch = useCustomerDispatch();

  useLayoutEffect(() => {
    if (!fixedBody) return;
    bodyElement.classList.add(styles['body--fixed']);
    return () => {
      bodyElement.classList.remove(styles['body--fixed']);
    };
  }, [fixedBody]);

  const canClose = !disallowClose || !(modalOptions as any).disallowClose;

  const classes = clsx(className, styles['modal'], {
    [styles['modal--full-width']]: width === 'full',
    [styles['modal--medium']]: width === 'medium',
    [styles['modal--narrow']]: width === 'narrow',
    [styles['modal--small']]: width === 'small',
  });

  const handleCloseModal = useCallback(() => {
    bodyElement.classList.remove('ReactModal__Body--open');
    bodyElement.classList.remove(styles['body--fixed']);
    if (closeModal) return closeModal();
    dispatch(defaultCloseModal());
  }, [closeModal]);

  return (
    <ReactModal
      {...rest}
      className={classes}
      contentLabel={contentLabel}
      isOpen={isModalOpen}
      onAfterClose={handleCloseModal}
      onRequestClose={canClose ? handleCloseModal : noop}
      overlayClassName={styles['modal-overlay']}
    >
      {!disallowClose && (
        <CloseButton onClick={handleCloseModal}>
          <SvgIcon icon="times" color={theme?.colors?.grays?.mid} size={16} />
        </CloseButton>
      )}
      {children(handleCloseModal)}
    </ReactModal>
  );
};
